import React, { useEffect, useState } from "react";
import { Button, Chip, Grid, IconButton, Tooltip } from "@material-ui/core";
import CustomInput from "components/CustomInput";
import { IconDollar, IconEdit } from "icons";
import LoadPricingStyles from "./LoadPricingStyles";
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import { useEditLoadPriceMutation } from "features/loads/loadsSlice";
import NotificationProvider from "components/NotificationProvider";
import FormatService from "services/formatService";
import { getStatusColor, LoadPriceStatusEnum, LoadPriceStatusNames, PricingModelTypesEnum } from "features/clients/clientConfigs/transports/PricingEnums";
import { useCalculateChargeFromCostQuery } from "features/clients/pricingRubricSlice";
import WorkOrderActions from "features/landing/components/WorkorderDashboard/WorkOrderCard/WorkOrderActions";

const LoadPricing = ({ loadPrice, title = null, loadId, groupId, clientConfig }) => {
    const classes = LoadPricingStyles();   
    
    const [price, setPrice] = useState();
    //original price is used to see if the price was edited
    const [originalPrice, setOriginalPrice] = useState({ cost: loadPrice?.cost, charge: loadPrice?.charge });
    const [editing, setEditing] = useState(false);

    const { data: calculatedCharge } = useCalculateChargeFromCostQuery({ clientId: clientConfig?.id, cost: price?.cost }, { skip: !price?.cost });
    const [editPrice, { isLoading: isSaving }] = useEditLoadPriceMutation();

    useEffect(() => {
        setPrice(loadPrice);
        setOriginalPrice({ cost: loadPrice?.cost, charge: loadPrice?.charge });
    }, [loadPrice]);

    const handleChange = (field, val) => {
        setPrice({ ...price, [field]: val });
    };

    const onEdit = () => {
        setEditing(true);
    }

    const priceHasChanged = (price) => {
        return price?.cost !== originalPrice?.cost || price?.charge !== originalPrice?.charge;
    }

    const handleSave = async () => {
        if(!priceHasChanged(price)) 
        {
            setEditing(false);
            return;
        } 
        let result = await editPrice({...price, loadId: loadId, status: LoadPriceStatusEnum.PENDING, loadGroupId: groupId});

        if (result && !result.error) {
            NotificationProvider.success("Price updated successfully");
            setEditing(false);
        } else {
            NotificationProvider.error("Failed to save the price");
        }
    }

    return (
        clientConfig?.config?.transportConfig?.pricingModelType === PricingModelTypesEnum.STANDARD ? null :
        (<Grid container className={classes.wrapper}>
            {title && <div item className={classes.legTitle}>{title}</div>}
            {editing ? (
                <Grid container direction="row" alignItems="center">
                    <Authorize profile={permissionProfiles.LOAD.LOAD_EDIT} >
                        <Grid item xs={3} className={classes.field}>
                            <CustomInput
                                id="cost"
                                label="Estimated Carrier Cost"
                                value={price?.cost ?? ""}
                                elementType="input"
                                onChange={(val) => handleChange("cost", +val)}
                                numberOnly
                                icon={<IconDollar />}
                                showEmpty
                            />
                        </Grid>
                    </Authorize>
                    <Grid item xs={3} className={classes.field}>
                        <CustomInput
                            id="charge"
                            label="Client Charge"
                            value={price?.charge ?? ""}
                            elementType="input"
                            onChange={(val) => handleChange("charge", val)}
                            numberOnly
                            icon={<IconDollar />}
                            showEmpty
                            disabled={clientConfig?.config?.transportConfig?.pricingModelType === PricingModelTypesEnum.LOWEST_BID}
                        />
                    </Grid>
                    <Grid item xs={3} className={classes.field}> 
                        {clientConfig?.config?.transportConfig?.pricingModelType === PricingModelTypesEnum.LOWEST_BID &&
                            <Button
                                onClick={() => {
                                    if (calculatedCharge) {
                                        handleChange("charge", calculatedCharge);
                                    }
                                }}
                                variant="outlined"
                                disabled={!price?.cost}
                                style={{ padding: 5 }}
                            >
                                Calculate Charge
                            </Button>
                        }
                    </Grid>
                    <Grid item xs={1} className={classes.field}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setPrice(originalPrice);
                                setEditing(false);
                            }}
                            style={{ marginLeft: -25 }}
                        >
                            Cancel
                        </Button>
                    </Grid>
                    <Grid item xs={1} className={classes.field}>
                        <Button
                            onClick={handleSave}
                            color="primary"
                            variant="contained"
                            disabled={isSaving || !priceHasChanged(price) 
                                || (clientConfig?.config?.transportConfig?.pricingModelType === PricingModelTypesEnum.LOWEST_BID && !price?.charge)}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </Grid>
                </Grid>
            ) : (
                <Grid container direction='row' alignItems="center">
                    <Authorize profile={permissionProfiles.LOAD.LOAD_EDIT} >
                        <Grid item xs={3} className={classes.readonlyField}>
                            <b>Estimated Carrier Cost:</b>
                            <div>{FormatService.formatCurrency(price?.cost ?? 0)}</div>
                        </Grid>
                    </Authorize>
                    <Grid item xs={1} className={classes.readonlyField}><b>Client Charge: </b> {FormatService.formatCurrency(price?.charge ?? 0)}</Grid>
                    <Authorize profile={permissionProfiles.LOAD.LOAD_EDIT} >
                        <Grid item xs={1} className={classes.readonlyField}>
                            {(clientConfig?.config?.transportConfig?.pricingModelType === PricingModelTypesEnum.PRICING_RUBRIC && loadPrice) ? (
                                <Tooltip title="The price is calculated using the pricing rubric and cannot be edited.">
                                    <span>
                                        <IconButton
                                            onClick={onEdit}
                                            color="primary"
                                            disabled
                                        >
                                            <IconEdit />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : (
                                <IconButton
                                    onClick={onEdit}
                                    color="primary"
                                >
                                    <IconEdit />
                                </IconButton>
                            )}
                        </Grid>
                    </Authorize>
                    {loadPrice && <Grid item xs={1}>
                            <Chip 
                                variant="outlined"
                                style={getStatusColor(loadPrice?.status)}
                                label={LoadPriceStatusNames[loadPrice?.status]}
                                color={"primary"}
                            />
                        </Grid>
                    }
                    <Authorize profile={permissionProfiles.LOAD.LOAD_APPROVE_PRICE}>
                        {loadPrice?.status == LoadPriceStatusEnum.PENDING && 
                            <Grid item xs={2} className={classes.field}>
                                <WorkOrderActions 
                                    entity={{...loadPrice, groupId: groupId}}
                                    loadMode={true}
                                    fill={false}
                                    handleDone={() => {}}
                                    wrapperStyles={classes.actionButtonWrapper}
                                    buttonStyles={classes.actionButtons}
                                />
                            </Grid>
                        }
                    </Authorize>
                </Grid>
            )}
        </Grid>)
    );
};

export default LoadPricing;